import { CacheManager } from "@Shared/cache";
import { Logger, LoggerService } from "@Shared/logger";
import { APP_EVENTS, STORAGE } from "@Core";
import { Bus } from "@Modules/event_module";
import { AUTH0_CONST, Auth0Service } from "@Modules/auth_module";
import { OptionsModule } from "@Modules/options_module";
import { UIModule } from "@Modules/ui_module";
import {
    AuthenticationConfigGlobal,
    SIGNIN_MODAL_CONST,
    SignInModalOptions,
    SignInModalPageOptions,
    SignInModalPageType,
} from "../_models";

const PAGE_CONFIG_KEYS: (keyof AuthenticationConfigGlobal)[] = [
    "enableSocialAllPages",
    "enableSocialHomepage",
    "enableSocialSection",
    "enableSocialArticle",
];

export class SignInModalService {
    private Logger: Logger = LoggerService.createLogger("SignInModalService");
    private static instance: SignInModalService;

    static get Instance(): SignInModalService {
        if (this.instance === null || this.instance === undefined) {
            this.instance = new SignInModalService();
        }
        return this.instance;
    }

    private options: SignInModalOptions;
    private container: HTMLElement | null = null;
    private removeKeyDownListener: (() => void) | null = null;

    constructor() {
        this.Logger.trace("****  CTOR ****");
    }

    // Built on demand so it picks up the site config, which loads after this singleton exists.
    private get dismissedCache(): CacheManager<boolean> {
        return new CacheManager<boolean>(STORAGE.signInModalDismissedKey, "localStorage", {
            type: "time",
            value: OptionsModule.Options.signInModalOptions.dismissedFor,
        });
    }

    public async init(): Promise<void> {
        this.options = this.resolveOptions();

        if (!this.options.enabled) {
            this.Logger.trace("Sign In Modal is disabled, skipping init");
            return;
        }

        if (this.isOneTapEnabled) {
            this.Logger.trace("One Tap is enabled, skipping Sign In Modal so the user is not prompted twice");
            return;
        }

        const path = window.location.pathname;
        if (path.startsWith("/login") || path.startsWith("/logout") || path.startsWith("/callback")) {
            this.Logger.trace(`On ${path}, skipping Sign In Modal`);
            return;
        }

        if (this.dismissedCache.getCache()) {
            this.Logger.trace("Sign In Modal was dismissed recently, skipping");
            return;
        }

        // The page-type fallback reads body classes, so the body has to exist before deciding.
        await this.whenBodyReady();
        if (!this.shouldShowOnPage) {
            this.Logger.trace("Sign In Modal is not targeted at this page type", {
                pageType: this.pageType,
                pages: this.options.pages,
            });
            return;
        }

        if (await this.isAuthenticated()) {
            this.Logger.trace("User already authenticated, skipping Sign In Modal");
            return;
        }

        await this.injectModal();
    }

    public async show(): Promise<void> {
        this.options = this.resolveOptions();
        await this.injectModal();
    }

    public close(dismissedByUser = false): void {
        if (!this.container) {
            return;
        }
        this.Logger.trace("Closing Sign In Modal", { dismissedByUser });
        this.container.remove();
        this.container = null;
        document.body.classList.remove(SIGNIN_MODAL_CONST.bodyOpenClass);
        this.removeKeyDownListener?.();
        this.removeKeyDownListener = null;

        if (dismissedByUser) {
            this.dismissedCache.setCache(true);
            Bus.emitAndTrack(
                APP_EVENTS.onSignInModalDismissed,
                { page: location.href, pageType: this.pageType },
                SIGNIN_MODAL_CONST.gtmDismissed
            );
        }
    }

    private async injectModal(): Promise<void> {
        if (document.getElementById(SIGNIN_MODAL_CONST.containerId)) {
            this.Logger.trace("Sign In Modal already injected");
            return;
        }

        const html = UIModule.HTML?.signInModal;
        if (!html) {
            this.Logger.warn("No Sign In Modal html available, skipping");
            return;
        }

        await this.whenBodyReady();

        const wrapper = document.createElement("div");
        wrapper.innerHTML = html;
        const container = wrapper.firstElementChild as HTMLElement | null;
        if (!container) {
            this.Logger.error("Sign In Modal template could not be parsed");
            return;
        }

        this.applyContent(container);
        document.body.appendChild(container);
        container.classList.add(SIGNIN_MODAL_CONST.visibleClass);
        document.body.classList.add(SIGNIN_MODAL_CONST.bodyOpenClass);
        this.container = container;
        this.registerListeners(container);

        this.Logger.trace("Sign In Modal displayed", { pageType: this.pageType });
        Bus.emitAndTrack(
            APP_EVENTS.onSignInModalDisplayed,
            { page: location.href, pageType: this.pageType },
            SIGNIN_MODAL_CONST.gtmDisplayed
        );
    }

    private applyContent(container: HTMLElement): void {
        const logo = this.options.logo;
        const logoElement = container.querySelector<HTMLImageElement>(SIGNIN_MODAL_CONST.logoSelector);
        if (logoElement) {
            if (logo) {
                logoElement.setAttribute("src", logo);
            } else {
                logoElement.remove();
            }
        }

        const subHeader = container.querySelector(SIGNIN_MODAL_CONST.subHeaderSelector);
        if (subHeader) {
            subHeader.textContent = this.options.subHeaderText || SIGNIN_MODAL_CONST.defaultSubHeaderText;
        }
    }

    private registerListeners(container: HTMLElement): void {
        container.querySelectorAll<HTMLElement>(SIGNIN_MODAL_CONST.closeSelector).forEach((element) => {
            element.addEventListener("click", () => this.close(true));
        });

        container.querySelectorAll<HTMLElement>(SIGNIN_MODAL_CONST.providerSelector).forEach((button) => {
            button.addEventListener("click", () => this.handleProviderClick(button.dataset.mngSigninProvider));
        });

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                this.close(true);
            }
        };
        document.addEventListener("keydown", onKeyDown);
        this.removeKeyDownListener = () => document.removeEventListener("keydown", onKeyDown);
    }

    private async handleProviderClick(provider: string): Promise<void> {
        const loginsource =
            provider === SIGNIN_MODAL_CONST.appleProvider
                ? AUTH0_CONST.loginSourceAppleSignIn
                : AUTH0_CONST.loginSourceGoogleOneTap;

        this.Logger.trace("Sign In Modal provider clicked", { provider, loginsource });
        Bus.emitAndTrack(APP_EVENTS.onSignInModalLoginStarted, { provider, loginsource }, SIGNIN_MODAL_CONST.gtmClick);

        // The provider opens in a popup, so this modal stays up behind it: if the reader closes the
        // popup without signing in, they are left where they started. Auth is determined before the
        // entitlement screen renders, which is the moment to take this down.
        const closeOnceAuthenticated = Bus.on(APP_EVENTS.onAuthDetermined, (result) => {
            if (result?.isAuthenticated) {
                this.close();
            }
        });

        try {
            const isAuthenticated = await Auth0Service.Instance.loginWithSocial(provider);
            if (isAuthenticated) {
                this.close();
            }
        } catch (error) {
            this.Logger.error("Sign In Modal login failed", error);
        } finally {
            closeOnceAuthenticated();
        }
    }

    private get isOneTapEnabled(): boolean {
        return (
            !!OptionsModule.Options?.oneTapOptions?.enabled ||
            this.toBoolean(window.authentication_config?.oneTapEnabled)
        );
    }

    private resolveOptions(): SignInModalOptions {
        const config = window.authentication_config;
        const resolved: SignInModalOptions = {
            enabled: this.toBoolean(config?.enableSocialSigninModal),
            subHeaderText: config?.subHeaderText || SIGNIN_MODAL_CONST.defaultSubHeaderText,
            logo: OptionsModule.Options?.ui?.logo,
            pages: this.resolvePages(config),
        };
        this.Logger.trace("Sign In Modal options", resolved);
        return resolved;
    }

    private resolvePages(config?: AuthenticationConfigGlobal): SignInModalPageOptions {
        // Targeting keys WordPress does not send yet must not silence a modal that was explicitly
        // enabled. Once any of them is present, the admin's choice is honoured as-is.
        const configured = !!config && PAGE_CONFIG_KEYS.some((key) => config[key] !== undefined);
        if (!configured) {
            return { allPages: true };
        }

        return {
            allPages: this.toBoolean(config.enableSocialAllPages),
            homePages: this.toBoolean(config.enableSocialHomepage),
            sectionPages: this.toBoolean(config.enableSocialSection),
            articlePages: this.toBoolean(config.enableSocialArticle),
        };
    }

    private get shouldShowOnPage(): boolean {
        const pages = this.options.pages;
        if (pages.allPages) {
            return true;
        }

        switch (this.pageType) {
            case "home":
                return !!pages.homePages;
            case "section":
                return !!pages.sectionPages;
            case "article":
                return !!pages.articlePages;
            default:
                return false;
        }
    }

    private get pageType(): SignInModalPageType {
        const declared = window.authentication_config?.pageType;
        if (declared === "home" || declared === "section" || declared === "article") {
            return declared;
        }

        // Fallback for hosts that don't declare it: WordPress' own body classes. Themes can drop
        // or rename these, which is why an explicit pageType is the better contract.
        const classes = document.body?.classList;
        if (!classes) {
            return "other";
        }
        if (classes.contains("home") || classes.contains("blog")) {
            return "home";
        }
        if (classes.contains("single") || classes.contains("single-post")) {
            return "article";
        }
        if (classes.contains("category") || classes.contains("tag") || classes.contains("archive")) {
            return "section";
        }
        return "other";
    }

    private isAuthenticated(): Promise<boolean> {
        return new Promise((resolve) => {
            let settled = false;
            const finish = (isAuthenticated: boolean) => {
                if (settled) {
                    return;
                }
                settled = true;
                unsubscribe();
                window.clearTimeout(timer);
                resolve(isAuthenticated);
            };

            const unsubscribe = Bus.on(APP_EVENTS.onAuthDetermined, (result) => finish(!!result?.isAuthenticated));
            const timer = window.setTimeout(() => {
                this.Logger.trace("onAuthDetermined not received, falling back to the cached Auth0 state");
                Auth0Service.Instance.isAuthenticated()
                    .catch(() => false)
                    .then((isAuthenticated) => finish(isAuthenticated));
            }, SIGNIN_MODAL_CONST.authDeterminedTimeoutMs);
        });
    }

    private whenBodyReady(): Promise<void> {
        if (document.body) {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            document.addEventListener("DOMContentLoaded", () => resolve(), { once: true });
        });
    }

    private toBoolean(value: string | boolean | undefined): boolean {
        return value === true || value === "1" || value === "true";
    }
}
